import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile } from 'node:fs/promises'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Launcher for Open Interpreter — natural language computer control */
export class OpenInterpreterLauncher implements ProviderLauncher {
  readonly name = 'open-interpreter'
  readonly displayName = 'Open Interpreter'
  readonly pricingHint = '🆓 Gratuit (open-source, clé API personnelle)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('interpreter', ['--version'])
      return true
    } catch {
      return false
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('interpreter', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    const args: string[] = []

    args.push('--custom_instructions', systemPrompt)

    if (config.model) {
      args.push('--model', config.model)
    }

    if (config.permissionMode === 'bypassPermissions') {
      args.push('-y')
    }

    const recorder = new SessionRecorder()

    const ptyProc = getPty().spawn(await resolveBinaryOrFallback('interpreter'), args, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(({ exitCode, signal }) => {
            cleanup()
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
      },
      recorder,
    }
  }
}
